/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * The order band: what a pending order across the model costs, and the action that commits it.
 *
 * ONE action for the whole set, per the footer band (`15905:53279`). Not one action per row: the
 * buyer's unit of work is the order across the model, and a per-row action would turn one intent into
 * a dozen submissions.
 *
 * `Save to List` sits beside it as a SHELL: placed, labelled and gated exactly as the order action is,
 * with nothing behind it. What it does — a wishlist write, a saved order, something else — is
 * CRCB2B-127's to establish. A control that looks conditional rather than dead is the honest way to
 * hold the position the frame gives it.
 *
 * Stacked and full-width below `lg`, a row from it, per the two frames.
 *
 * PRESENTATIONAL, and strictly so. It holds no state, reads no catalogue and owns no submission: the
 * figures arrive computed, and the fetcher's own `Form` arrives as a prop. What follows a submission —
 * publishing the basket revision, reporting the outcome — belongs to whoever owns the fetcher, because
 * none of it is presentation. That keeps the band's own behaviour small enough to test directly, which
 * is the whole reason it stopped being markup inside the table.
 */
import type { ReactElement } from 'react';
import type { FetcherWithComponents } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PRICE_PLACEHOLDER, type OrderLine } from '@/lib/product/variations-table';
import { cn } from '@/lib/utils';

/**
 * The `Form` a fetcher supplies, taken as a prop.
 *
 * `fetcher.Form` rather than a bare `<form>` or an imperative submit, so the action carries the payload
 * the same way with or without JavaScript, and so adding to the basket does not navigate away from the
 * product page the buyer is still working on. The generic is irrelevant here — the component's type
 * does not vary with the action's response — so `unknown` keeps this independent of what the owner
 * typed its fetcher on.
 */
type FetcherForm = FetcherWithComponents<unknown>['Form'];

interface ProductOrderBarProps {
    /** The lines the action will submit. Serialised as-is: the bar does not reshape what it forwards. */
    lines: readonly OrderLine[];
    /**
     * The running total, already formatted for the active locale and currency — or `undefined` where it
     * cannot be stated in full, which the band renders as the placeholder rather than as a figure.
     *
     * Formatted rather than numeric on purpose: a bare number here would make this component decide a
     * currency, which is a catalogue question it is deliberately blind to.
     */
    total: string | undefined;
    /** How many of the model's items the action will add, counted as lines rather than as units. */
    itemCount: number;
    /** Whether a submission is in flight, so one intent cannot become two basket writes. */
    submitting: boolean;
    /** The owner's fetcher `Form`; see `FetcherForm`. */
    Form: FetcherForm;
    /** The action the form posts to. */
    action: string;
    className?: string;
}

export default function ProductOrderBar({
    lines,
    total,
    itemCount,
    submitting,
    Form,
    action,
    className,
}: ProductOrderBarProps): ReactElement {
    const { t } = useTranslation('product');

    /**
     * When neither action can fire. Three conditions, and the third is the load-bearing one.
     *
     * Nothing selected is not an error state to explain — it is the resting state of every table, so the
     * actions simply cannot fire yet. A submission already in flight cannot be repeated, or one intent
     * becomes two basket writes.
     *
     * And an UNSTATABLE total withholds them: `total` is `undefined` only when a selected row carries no
     * usable price, and a page that has just declined to state what an order costs must not ask the buyer
     * to commit to it. An empty selection is not this case — it states a zero — so the two conditions
     * stay independent.
     *
     * ONE expression read by BOTH buttons, so they cannot disagree about whether a selection exists. Two
     * controls side by side, one live and one dead on the same selection, would read as a broken page
     * rather than as a rule.
     *
     * AVAILABILITY IS DELIBERATELY NOT HERE, and the omission is the decision. Fulfilment DIVIDES an
     * oversized order rather than refusing it: three on hand against a request for 500 ships three and
     * backorders the rest. The availability this page shows is a nightly snapshot, so a storefront gate
     * would refuse — on figures hours out of date — an order the warehouse would have accepted. The rows
     * STATE what they know beside fields that will take more than that, and where the platform does
     * refuse, its own reason is surfaced. Adding a stock check here would reverse a settled decision.
     */
    const actionsUnavailable = !lines.length || submitting || total === undefined;

    return (
        <div
            className={cn(
                'flex flex-col gap-6 px-4 py-4 lg:flex-row lg:items-center lg:gap-12',
                /*
                 * PINNED below `lg`, in flow from it.
                 *
                 * A buyer enters quantities down a list longer than the screen, and the action that
                 * commits them sits after the last row — below every row they have not reached yet. So
                 * below `lg` the band rides the bottom of the viewport while the set is on screen.
                 *
                 * `sticky`, never `fixed`. Sticky is still IN FLOW: it is pinned only while its own
                 * section is in view and releases as the section scrolls past, so it obstructs nothing
                 * beyond the table it belongs to. A fixed bar would overlay the page at every scroll
                 * position, including whatever sits below this section. It also needs no portal, no
                 * measured viewport and no JavaScript — the band is the last child of its section, so
                 * the browser pins it against that box unaided.
                 *
                 * Opaque background and a top rule because rows now pass UNDERNEATH it. Without the
                 * fill, row text would scroll through the band and neither would be readable.
                 *
                 * `z-10` is deliberately modest. The header is `sticky top-0 z-50` at the other end of
                 * the viewport and the two never meet; a high value here would only put this band over
                 * overlays that have every right to cover it. The class is inert from `lg`, where the
                 * element is static and z-index does not apply.
                 *
                 * NOTE: sticky fails SILENTLY against a clipping ancestor — any `overflow` other than
                 * `visible` between here and the scroll container turns this into an ordinary block,
                 * with no error anywhere. This band renders inside `TabsContent` inside a grid item, and
                 * jsdom applies no stylesheets, so no unit test or snapshot can establish that it holds.
                 * Verified in the real app instead.
                 */
                'sticky bottom-0 z-10 border-t border-border bg-background lg:static lg:border-t-0',
                className
            )}
            data-testid="product-variations-order">
            <div className="flex flex-1 flex-col items-start">
                {/* `components/label-sm` — 14px on a 20px line box at 0.1em, capitalised, on the
                    secondary tier, per the frame. */}
                <span className="text-sm font-book leading-5 tracking-[0.1em] text-muted-foreground capitalize">
                    {t('orderTotal')}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                    {/* `body/lg-bold` — the only figure on the band at full weight. */}
                    <span
                        className="text-lg leading-[1.5] font-bold tracking-sram text-foreground"
                        data-testid="product-variations-order-total">
                        {total ?? PRICE_PLACEHOLDER}
                    </span>
                    {/* The basis, not decoration: a row carries three price points, and a total that
                        does not say which one it summed is unreadable.

                        This badge appears in NEITHER frame, where the slot holds a pricing tier
                        instead — a knowing divergence. The tier cannot be stated (below), and between
                        an empty slot and one naming which price was summed, the second says something
                        true about the figure beside it. */}
                    <Badge variant="accent">{t('dealerPrice')}</Badge>

                    {/* NO PRICING-TIER BADGE, and this is a data gap rather than a missing render.
                        The frames draw `Bronze Tier Price` here. A tier is a property of the DEALER
                        placing the order, and this instance has no dealer identity to read one from —
                        there is no account-level tier on any payload the page receives, and none to
                        derive. `product-list` carries a `TODO(CRCB2B-87)` against the same mock tier,
                        so the gap is shared rather than local to this band. Recorded as BLOCKED on
                        CRCB2B-118 rather than attempted: a tier invented here would misprice an order
                        in a way the buyer would have no way to question.

                        TODO(CRCB2B-127): the frame's SECOND badge, `MAP Protected`, desktop only
                        (`15905:53286`). It needs a minimum-advertised-price protection status on a
                        payload the page already reads — a per-product or per-variant flag the
                        catalogue does not currently carry. Not rendered, and deliberately not faked:
                        a badge asserting legal price protection is unfalsifiable by the reader and
                        consequential if believed, which makes invented data worse here than an absent
                        badge. PARKED on CRCB2B-127, which owns the other unfinished thing on this band
                        and is the closest open item — not because a save action and a price-protection
                        badge are the same work. Whether this deserves its own ticket is an open
                        question in `design.md`; move the id here when one exists. */}
                </div>
            </div>

            {/* Both actions, in ONE group whose visual order inverts at `lg`.

                DOM order is the mobile frame's — `Add to Cart` first, `Save to List` beneath it
                (`13985:38388`) — because that is the order a screen reader and the tab sequence should
                follow at every width: the primary action leads. The desktop frame draws them the other
                way round, `Save to List` at 183px wide then `Add to Cart` (`15905:53292`), so from `lg`
                the row is reversed visually and the DOM is left alone.

                `flex-row-reverse` rather than two orderings of two elements, and rather than duplicating
                both buttons behind a breakpoint: one DOM means one tab order and one set of controls in
                the accessibility tree. The table above does dual-render, but a table and a card list are
                genuinely different markup — two buttons are not.

                8px apart on the stack, 16px in the row, per the two frames. */}
            <div className="flex w-full flex-col gap-2 lg:w-auto lg:flex-row-reverse lg:items-center lg:gap-4">
                <Form method="post" action={action} className="w-full lg:w-auto">
                    <input type="hidden" name="productItems" value={JSON.stringify(lines)} />
                    <Button
                        type="submit"
                        // `brand`, not the default fill. The frame's Button Group paints
                        // `--background-color-fill-brand` #e51937 (`15905:53292`), which is
                        // `--base-core-sram-red` and exactly what `bg-brand` resolves to; the default
                        // variant is `--primary` #141414, near-black.
                        //
                        // This DIVERGES from the buy box's own Add to Cart, which takes the default
                        // fill — the two sit on one page and now read differently. That is the frame's
                        // instruction rather than an oversight here: the table is the B2B purchase
                        // path and its commit is drawn as the branded action. Worth reconciling with
                        // design if the two are meant to match; changing this one back is a one-word
                        // edit, and changing the other is not this component's call.
                        variant="brand"
                        size="lg"
                        className="w-full lg:w-auto"
                        disabled={actionsUnavailable}
                        data-testid="product-variations-order-submit">
                        {t('addItemsToCart', { count: itemCount })}
                    </Button>
                </Form>

                {/* TODO(CRCB2B-127): a shell. The control is placed, labelled and gated; what it DOES —
                    a wishlist write, a saved order, something else — is that ticket's to establish, and
                    is why there is no handler here.

                    `type="button"` is load-bearing rather than conventional: this sits inside the band
                    beside a form, and a default-type button that ever moved inside that form would
                    submit the order it is not supposed to touch.

                    `outline` per the frame — a bordered control on the page's own background, against
                    the filled primary beside it. It carries no `aria-disabled` in place of `disabled`:
                    the action does not exist yet, so there is nothing to explain to a reader who
                    focuses it. */}
                <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="w-full lg:w-auto"
                    disabled={actionsUnavailable}
                    data-testid="product-variations-order-save">
                    {/* The SAME count as the order action, from the same value — both name how many of
                        the model's items they act on, and two figures that disagreed on one band would
                        leave the buyer unable to tell which described their selection. */}
                    {t('saveToList', { count: itemCount })}
                </Button>
            </div>
        </div>
    );
}
